import { useParams, Link, Navigate } from "react-router-dom";
import { Check, Phone, ArrowRight, MapPin, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/site/SEO";
import QuoteForm from "@/components/site/QuoteForm";
import { SERVICE_AREAS, SORTED_AREAS, SERVICES, COMPANY } from "@/lib/constants";

export default function LocationDetail() {
  const { slug } = useParams();
  const area = SERVICE_AREAS.find((a) => a.slug === slug);
  if (!area) return <Navigate to="/service-areas" replace />;

  const loc = `${area.city}, ${area.state}`;
  const nearby = SORTED_AREAS.filter((a) => a.slug !== slug).slice(0, 6);

  const schema = {
    "@context": "https://schema.org",
    "@type": "GeneralContractor",
    name: `${COMPANY.name} — ${loc}`,
    telephone: COMPANY.phone,
    areaServed: loc,
    description: area.metaDescription,
    address: { "@type": "PostalAddress", addressLocality: COMPANY.city, addressRegion: COMPANY.state },
  };

  return (
    <div>
      <SEO title={area.seoTitle} description={area.metaDescription} schema={schema} />

      <section className="bg-secondary text-white grain-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="flex items-center gap-2 text-sm text-white/60 mb-4">
            <Link to="/service-areas" className="hover:text-primary">Service Areas</Link> <span>/</span> <span className="text-white">{loc}</span>
          </div>
          {area.primary && <span className="inline-block bg-primary text-white text-xs font-bold px-3 py-1 tracking-wider mb-3">HOME BASE</span>}
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold max-w-3xl">Concrete Contractor in {loc}</h1>
          <p className="text-white/80 mt-5 text-lg max-w-2xl">{area.intro}</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6 text-sm text-white/70">
            <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> {area.county}</span>
            <span className="flex items-center gap-2"><Navigation className="h-4 w-4 text-primary" /> {area.distance}</span>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-10">
          <div className="bg-primary/5 border-l-4 border-primary p-6">
            <p className="text-lg leading-relaxed font-medium">{area.highlight}</p>
          </div>

          {area.landmarks?.length > 0 && (
            <div>
              <h2 className="font-heading text-2xl font-bold mb-4">Serving the Whole {area.city} Area</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                From the heart of {area.city} to the surrounding neighborhoods, our crew works throughout the community — including areas near {area.landmarks.join(", ")}.
              </p>
              <div className="flex flex-wrap gap-2">
                {area.landmarks.map((l) => (
                  <span key={l} className="text-sm font-semibold bg-card border border-border px-3 py-1.5 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-primary" /> {l}</span>
                ))}
              </div>
            </div>
          )}

          <div>
            <h2 className="font-heading text-2xl font-bold mb-5">Services We Offer in {area.city}</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {SERVICES.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className="flex items-center justify-between bg-card border border-border p-4 hover:border-primary transition-colors group" data-testid={`loc-service-${s.slug}`}>
                  <span className="flex items-center gap-2 font-medium"><Check className="h-4 w-4 text-primary" /> {s.title}</span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                </Link>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading text-2xl font-bold mb-3">Why {area.city} Chooses CG Concrete</h2>
            <p className="text-muted-foreground leading-relaxed">
              We're local — {area.distance} — so we know {area.county}'s soils, weather, and permitting. That means concrete poured right the first time, backed by a licensed and insured team that treats your property like our own. Every {area.city} project gets proper base prep, reinforcement, and drainage built for Wisconsin's freeze-thaw seasons.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <span className="text-sm text-muted-foreground self-center mr-2">Nearby areas:</span>
            {nearby.map((a) => (
              <Link key={a.slug} to={`/service-areas/${a.slug}`} className="text-sm font-semibold px-3 py-1.5 border border-border hover:border-primary hover:text-primary transition-colors">{a.city}</Link>
            ))}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-24">
            <div className="bg-secondary text-white p-6 mb-4">
              <h3 className="font-heading text-xl font-bold">Free Quote in {area.city}</h3>
              <Button asChild variant="outline" className="mt-4 w-full bg-transparent border-white/40 text-white hover:bg-white hover:text-secondary rounded-sm font-bold">
                <a href={`tel:${COMPANY.phoneRaw}`}><Phone className="mr-2 h-4 w-4" /> {COMPANY.phone}</a>
              </Button>
            </div>
            <QuoteForm defaultService="" extraInspiration={[]} />
          </div>
        </div>
      </div>
    </div>
  );
}
